import React, { useState } from 'react';
import useScrollReveal, { useStaggerReveal } from '../hooks/useScrollReveal';

const WeatherWiseLog = () => {
  const [expandedItems, setExpandedItems] = useState({});
  const [headerRef, headerVisible] = useScrollReveal();
  const [capsRef, , getCapItemProps] = useStaggerReveal(5, { staggerDelay: 0.1 });
  const [midRef, midVisible] = useScrollReveal();
  const [logRef, , getLogItemProps] = useStaggerReveal(5, { staggerDelay: 0.15 });

  const toggleItem = (id) => {
    setExpandedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const capabilities = [
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      iconBg: 'bg-teal-50 text-teal-600 border-teal-200',
      cardBg: 'bg-teal-50/60',
      borderColor: 'border-teal-200',
      title: 'Live data surfacing',
      description: 'Pulls current observations, model-generated forecasts, and severe weather alerts from Xweather MCP and presents them conversationally',
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      ),
      iconBg: 'bg-blue-50 text-blue-600 border-blue-200',
      cardBg: 'bg-blue-50/60',
      borderColor: 'border-blue-200',
      title: 'Cross-location analysis',
      description: 'Compare conditions across multiple cities with synthesized severity rankings and contextual summaries',
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      iconBg: 'bg-purple-50 text-purple-600 border-purple-200',
      cardBg: 'bg-purple-50/60',
      borderColor: 'border-purple-200',
      title: 'Science-backed answers',
      description: 'Responses grounded in NOAA, NASA, NCA5, and AMS publications via a retrieval-augmented generation pipeline',
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      iconBg: 'bg-amber-50 text-amber-600 border-amber-200',
      cardBg: 'bg-amber-50/60',
      borderColor: 'border-amber-200',
      title: 'Hazard-aware guidance',
      description: 'Travel-conscious safety briefings with human-in-the-loop review before surfacing high-severity alerts',
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      iconBg: 'bg-red-50 text-red-500 border-red-200',
      cardBg: 'bg-red-50/60',
      borderColor: 'border-red-200',
      title: 'Derived computations',
      description: 'On-the-fly unit conversions, calculated metrics like wind chill and heat index, and numerical reasoning over model output values',
    },
  ];

  const devLogCategories = [
    {
      id: 'foundation',
      title: 'Foundation',
      status: 'Shipped',
      color: 'from-teal-500 to-cyan-500',
      dotColor: 'bg-teal-500',
      lineColor: 'border-teal-200',
      statusBg: 'bg-teal-100 text-teal-700 border-teal-200',
      cardBg: 'bg-teal-50/40',
      cardBorder: 'border-teal-200',
      tagBg: 'bg-teal-50',
      tagBorder: 'border-teal-200',
      titleColor: 'text-teal-700',
      items: [
        {
          id: 'f1',
          title: 'Xweather MCP Integration',
          description: 'Connected the agent to Vaisala Xweather through LangChain MCP adapters over streamable HTTP, unlocking 16 weather tools spanning forecasts, air quality, and alert feeds.',
          tags: ['LangGraph', 'MCP', 'Xweather', 'Pydantic', 'Async'],
        },
        {
          id: 'f2',
          title: 'MCP Bridge & Calculator Tool',
          description: 'Stood up a lightweight bridge to the remote Xweather endpoint with query-param authentication, paired with a numexpr-powered calculator for on-the-fly unit conversions.',
          tags: ['MCP', 'numexpr', 'Tool Use', 'Streamable HTTP'],
        },
        {
          id: 'f3',
          title: 'Intent Router & Agent Graph',
          description: 'Designed an LLM-driven intent classifier that produces structured output to route queries, then wired the full LangGraph agent graph with per-intent system prompts and dynamic tool binding.',
          tags: ['LangGraph', 'Structured Output', 'Intent Classification'],
        },
      ],
    },
    {
      id: 'rag',
      title: 'RAG Pipeline',
      status: 'Shipped',
      color: 'from-purple-500 to-pink-500',
      dotColor: 'bg-purple-500',
      lineColor: 'border-purple-200',
      statusBg: 'bg-purple-100 text-purple-700 border-purple-200',
      cardBg: 'bg-purple-50/40',
      cardBorder: 'border-purple-200',
      tagBg: 'bg-purple-50',
      tagBorder: 'border-purple-200',
      titleColor: 'text-purple-700',
      items: [
        {
          id: 'r1',
          title: 'Knowledge Ingestion Pipeline',
          description: 'Developed an end-to-end pipeline that parses, summarizes, and embeds atmospheric science PDFs from NOAA, NASA, and AMS into a ChromaDB vector store, exposed through a dedicated retrieval tool.',
          tags: ['RAG', 'ChromaDB', 'Embeddings', 'PDF Parsing'],
        },
        {
          id: 'r2',
          title: 'RAG-Grounded Explanations',
          description: 'Plugged retrieval-augmented generation into the agent\'s reasoning path so it can blend knowledge-base context from published research with live weather observations for scientifically grounded responses.',
          tags: ['RAG', 'LangChain', 'Retrieval', 'Grounding'],
        },
      ],
    },
    {
      id: 'observability',
      title: 'Observability',
      status: 'Shipped',
      color: 'from-orange-500 to-amber-500',
      dotColor: 'bg-orange-500',
      lineColor: 'border-orange-200',
      statusBg: 'bg-orange-100 text-orange-700 border-orange-200',
      cardBg: 'bg-orange-50/40',
      cardBorder: 'border-orange-200',
      tagBg: 'bg-orange-50',
      tagBorder: 'border-orange-200',
      titleColor: 'text-orange-700',
      items: [
        {
          id: 'o1',
          title: 'LangSmith Agent Tracing',
          description: 'Wired in LangSmith with config validation, run metadata helpers, and trace tags across every node in the agent graph for full end-to-end inspection.',
          tags: ['LangSmith', 'Observability', 'Tracing'],
        },
      ],
    },
    {
      id: 'in-progress',
      title: 'In Progress',
      status: 'In Progress',
      color: 'from-blue-500 to-sky-500',
      dotColor: 'bg-blue-500',
      lineColor: 'border-blue-200',
      statusBg: 'bg-blue-100 text-blue-700 border-blue-200',
      cardBg: 'bg-blue-50/40',
      cardBorder: 'border-blue-200',
      tagBg: 'bg-blue-50',
      tagBorder: 'border-blue-200',
      titleColor: 'text-blue-700',
      items: [
        {
          id: 'ip1',
          title: 'Multi-Agent Specialization',
          description: 'Breaking the monolithic agent into specialized sub-agents for forecasts, alerts, climate science Q&A, and travel planning, orchestrated by a supervisor node in LangGraph.',
          tags: ['LangGraph', 'Multi-Agent', 'Supervisor Pattern'],
        },
        {
          id: 'ip2',
          title: 'Evaluation Framework & Golden Test Suite',
          description: 'Building a structured evaluation harness with golden question–answer pairs and automated scoring to measure accuracy, grounding, and tool-use correctness across agent responses.',
          tags: ['Evaluation', 'Testing', 'LangSmith', 'Quality'],
        },
      ],
    },
    {
      id: 'planned',
      title: 'Up Next',
      status: 'Planned',
      color: 'from-amber-500 to-yellow-500',
      dotColor: 'bg-amber-500',
      lineColor: 'border-amber-200',
      statusBg: 'bg-amber-100 text-amber-700 border-amber-200',
      cardBg: 'bg-amber-50/40',
      cardBorder: 'border-amber-200',
      tagBg: 'bg-amber-50',
      tagBorder: 'border-amber-200',
      titleColor: 'text-amber-700',
      items: [
        {
          id: 'p1',
          title: 'Cross-Model Evaluation Pipeline',
          description: 'Systematically compare agent responses across Claude, GPT-4, and Gemini to benchmark accuracy, latency, and grounding quality on the golden test suite.',
          tags: ['Evaluation', 'Benchmarking', 'Multi-Model'],
        },
        {
          id: 'p2',
          title: 'Expanded MCP Ecosystem',
          description: 'Integrate additional MCP servers beyond Xweather — satellite imagery, ocean buoy data, and air quality networks — to broaden the agent\'s observational coverage.',
          tags: ['MCP', 'Data Sources', 'Integration'],
        },
        {
          id: 'p3',
          title: 'Dynamic Visualizations',
          description: 'Generate interactive maps, time-series charts, and radar composites on the fly so users can see the data the agent is reasoning over.',
          tags: ['Visualization', 'Charts', 'Maps'],
        },
        {
          id: 'p4',
          title: 'Production Deployment',
          description: 'Ship a publicly accessible web app with authentication, rate limiting, and monitoring on GCP — turning the prototype into a production-grade service.',
          tags: ['GCP', 'Deployment', 'Production', 'DevOps'],
        },
      ],
    },
  ];

  const techStack = ['LangGraph', 'Claude Sonnet 4 / 3.7', 'MCP', 'LangSmith', 'Pydantic v2', 'Python 3.11+', 'GCP', 'Pinecone', 'uv'];

  const hashtags = ['#WeatherWiseAI', '#LangGraph', '#MCP', '#RAG', '#AIAgents'];

  return (
    <section id="weatherwise" className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-80 h-80 bg-gradient-to-br from-cyan-200/30 to-teal-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-amber-200/20 to-orange-200/20 rounded-full blur-3xl" />
      </div>

      <div className="max-w-5xl mx-auto relative">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-12 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">
            <span className="bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">WeatherWise.ai</span>
          </h2>
          <p className="text-gray-500 text-base mb-8">
            Built in collaboration with{' '}
            <a
              href="https://www.yavarkhan.me/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-teal-600 font-medium hover:text-teal-700 underline decoration-teal-300"
            >
              Yavar Khan
            </a>
          </p>

          {/* Quote */}
          <div className="max-w-2xl mx-auto mb-8 px-8 py-6 rounded-2xl border border-gray-200 bg-white/80">
            <p className="text-lg md:text-xl italic text-gray-700 mb-2">
              &ldquo;The best way to have a good idea is to have lots of data.&rdquo;
            </p>
            <p className="text-sm text-gray-500">Adapted from Linus Pauling</p>
          </div>

          <p className="text-gray-600 text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
            WeatherWise.ai is a conversational interface over real weather model outputs. It doesn&rsquo;t generate its own
            forecasts &mdash; it reads from operational NWP models and observational feeds via Xweather, then explains them
            in plain language backed by published atmospheric science research. Ask a question, and the agent surfaces the
            right data, interprets it, and cites its sources.
          </p>
        </div>

        {/* Key Capabilities */}
        <div ref={capsRef} className="mb-12">
          <h3 className="text-center text-sm font-bold text-gray-500 tracking-widest uppercase mb-6">Key Capabilities</h3>
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            {capabilities.slice(0, 3).map((cap, i) => {
              const itemProps = getCapItemProps(i);
              return (
                <div key={i} className={`${cap.cardBg} rounded-2xl border ${cap.borderColor} shadow-sm hover:shadow-md p-5 ${itemProps.className}`} style={itemProps.style}>
                  <div className={`w-10 h-10 rounded-xl ${cap.iconBg} border flex items-center justify-center mb-3`}>
                    {cap.icon}
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">{cap.title}</h4>
                  <p className="text-sm text-gray-600">{cap.description}</p>
                </div>
              );
            })}
          </div>
          <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {capabilities.slice(3).map((cap, i) => {
              const itemProps = getCapItemProps(i + 3);
              return (
                <div key={i} className={`${cap.cardBg} rounded-2xl border ${cap.borderColor} shadow-sm hover:shadow-md p-5 ${itemProps.className}`} style={itemProps.style}>
                  <div className={`w-10 h-10 rounded-xl ${cap.iconBg} border flex items-center justify-center mb-3`}>
                    {cap.icon}
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">{cap.title}</h4>
                  <p className="text-sm text-gray-600">{cap.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Testimonial + Tech Stack */}
        <div
          ref={midRef}
          className={`grid md:grid-cols-2 gap-4 mb-12 transition-all duration-700 ${midVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Yavar's testimonial */}
          <div className="bg-amber-50/50 rounded-2xl border border-amber-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xs font-bold">YK</div>
              <div>
                <p className="text-sm font-bold text-gray-900">Yavar Khan</p>
                <p className="text-xs text-gray-500">Launch Announcement</p>
              </div>
              <a
                href="https://www.linkedin.com/posts/yavar-khan29_genai-aiagents-langgraph-activity-7426743062099369984-sDfE?utm_source=share&utm_medium=member_desktop&rcm=ACoAABTruQIBW5Fplzfa9X7v5zsIgN8G0Mw0nuE"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
            <p className="text-gray-700 text-sm italic leading-relaxed mb-4">
              &ldquo;Google Gemini estimated &lsquo;90% and 100%&rsquo; after admitting it couldn&rsquo;t locate the data. WeatherWise returned the precise figure: 32%, pulled from real meteorological records.&rdquo;
            </p>
            <div className="flex flex-wrap gap-2">
              {hashtags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-600">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Tech Stack */}
          <div className="bg-cyan-50/50 rounded-2xl border border-cyan-200 shadow-sm p-6">
            <h3 className="text-sm font-bold text-cyan-700 tracking-widest uppercase mb-4">Tech Stack</h3>
            <div className="flex flex-wrap gap-2 mb-6">
              {techStack.map((tech) => (
                <span key={tech} className="px-3 py-1.5 rounded-full text-xs font-medium bg-white text-gray-700 border border-cyan-200">
                  {tech}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className="font-medium">Private Repo &middot; VectorWorkX</span>
            </div>
          </div>
        </div>

        {/* Development Log */}
        <div ref={logRef} className="space-y-8">
          {devLogCategories.map((category, catIndex) => {
            const logItemProps = getLogItemProps(catIndex);
            return (
            <div key={category.id} className={logItemProps.className} style={logItemProps.style}>
              {/* Category Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-3 h-3 rounded-full ${category.dotColor}`} />
                <h3 className="text-xl font-bold text-gray-900">{category.title}</h3>
                <span className={`text-xs px-2.5 py-0.5 rounded-full border font-medium ${category.statusBg}`}>
                  {category.status}
                </span>
              </div>

              {/* Category Items */}
              <div className={`ml-1.5 border-l-2 ${category.lineColor} pl-6 space-y-4`}>
                {category.items.map((item) => {
                  const isExpanded = expandedItems[item.id] !== false; // default expanded
                  return (
                    <div
                      key={item.id}
                      className={`relative ${category.cardBg} rounded-2xl border ${category.cardBorder} shadow-sm p-5 transition-all duration-300 hover:shadow-md`}
                    >
                      {/* Timeline dot */}
                      <div className={`absolute -left-[calc(1.5rem+5px)] top-6 w-2 h-2 rounded-full ${category.dotColor} opacity-50`} />

                      <div
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => toggleItem(item.id)}
                      >
                        <h4 className={`text-base font-bold ${category.titleColor}`}>{item.title}</h4>
                        <svg
                          className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </div>

                      {isExpanded && (
                        <div className="mt-3">
                          <p className="text-gray-600 text-sm mb-3 leading-relaxed">{item.description}</p>
                          <div className="flex flex-wrap gap-2">
                            {item.tags.map((tag) => (
                              <span key={tag} className={`px-2.5 py-1 rounded-full text-xs font-medium ${category.tagBg} text-gray-700 border ${category.tagBorder}`}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
          })}
        </div>
      </div>
    </section>
  );
};

export default WeatherWiseLog;
